<template>
  <section
    class="form-multi-select is-relative m-b-25"
    :class="{ 'highlight-error': forceShowError }"
  >
    <ValidationProvider
      ref="validationProvider"
      :rules="rules"
      v-slot="validationProps"
      :name="config.label || config.placeholder || 'field'"
      :value="computedValue"
    >
      <float-label
        :label="config.placeholder"
        :on="showFloatLabel"
        :input-is-disabled="disabled"
        :multi-select-default-active="!!value"
      >
        <div :class="getClasses(validationProps)" @click.stop>
          <FieldView class="form-multi-select">
            <vue-multiselect
              :class="{ 'multiple-group-select': config.multiple && config.groupSelect }"
              ref="multiselect"
              :model-value="selectedOptions"
              :options="options"
              :loading="isLoading"
              :multiple="config.multiple"
              :internal-search="config.internalSearch"
              :show-no-results="!pinAddOption"
              :show-no-options="!pinAddOption"
              :group-values="config.groupValues"
              :group-select="config.groupSelect"
              :show-labels="config.showLabels"
              :preselect-first="config.preselectFirst"
              :group-label="config.groupLabel"
              :custom-label="config.customLabel"
              :placeholder="config.placeholder"
              :track-by="config.trackBy"
              :label="config.label"
              :select-label="config.selectLabel"
              :select-group-label="config.selectGroupLabel"
              :allow-empty="config.allowEmpty"
              :disabled="disabled"
              :preserve-search="config.preserveSearch"
              :close-on-select="config.closeOnSelect"
              :clear-on-select="config.clearOnSelect"
              :limit="config.limit"
              :limit-text="config.limitText"
              @select="onOptionClicked"
              @remove="onRemove"
              @search-change="onSearchChange"
              @close="onClose"
            >
              <!-- To disabled certain options, the option object should have $isDisabled: true
              and this slot should be used -->
              <template v-if="$slots.option" #option="props">
                <div v-if="props.option.isAddOption" class="is-flex is-align-items-center">
                  <i
                    class="icon cls cls-add is-size-7 m-r-8 has-text-link has-background-grey-lightest is-circular plus is-inline-flex is-align-items-center is-justify-content-center has-line-height-1"
                  />
                  <slot name="addOption" v-bind="props">{{ $t('core.add') }}</slot>
                </div>
                <template v-else-if="config.isAsync && props.option.isLoadMoreButton">
                  <FormMultiSelectLoadMoreSection
                    :config="config"
                    :is-loading="isLoading"
                    :option="props.option"
                    @on-load-more="onLoadMore"
                  />
                </template>
                <span
                  v-else
                  :class="{ disabled: props.option.$isDisabled }"
                  :data-e2e="FormCypressAttribute.views.FORM_MULTISELECT_OPTION"
                >
                  <slot name="option" v-bind="props" />
                </span>
              </template>
              <template v-if="$slots.tag" #tag="props">
                <slot name="tag" v-bind="props" />
              </template>
              <template v-if="$slots.singleLabel" #singleLabel="props">
                <slot name="singleLabel" v-bind="props" />
              </template>
              <template v-if="$slots.selection" #selection="props">
                <slot name="selection" v-bind="props" />
              </template>
              <template v-if="$slots.placeholder" #placeholder>
                <slot name="placeholder" />
              </template>
              <template #noOptions>
                <slot name="noOptions">
                  <div>{{ config.noOptionsLabel }}</div>
                </slot>
              </template>
              <template #noResult="{ search }">
                <slot name="noResult" :search="search" :config="config" :enable-add="enableAdd">
                  <div class="m-b-10">{{ config.noResultLabel }}</div>
                  <FormSelectAddOptionRow
                    v-if="enableAdd && $slots.addOption"
                    class="m-t-5"
                    @click="onAddOptionClick"
                  >
                    <slot name="addOption" :option="{ isAddOption: true }" :search="search">
                      {{ $t('core.add') }}
                    </slot>
                  </FormSelectAddOptionRow>
                </slot>
              </template>
              <template v-if="pinAddOption" #afterList>
                <div
                  class="pinned-add-option has-background-white xy-b-0"
                  :data-e2e="FormCypressAttribute.views.FORM_SELECT_PINNED_ADD_OPTION"
                >
                  <div v-if="showEmptyState">
                    <slot name="emptyState">
                      <div>{{ config.noOptionsLabel }}</div>
                    </slot>
                  </div>
                  <div v-else-if="showNoMatchState">
                    <slot name="noMatchState" :search="searchQuery">
                      <div>{{ config.noResultLabel }}</div>
                    </slot>
                  </div>
                  <FormSelectAddOptionRow v-if="enableAdd" @click="onAddOptionClick">
                    <slot name="addOption" :option="{ isAddOption: true }" :search="searchQuery">
                      {{ $t('core.add') }}
                    </slot>
                  </FormSelectAddOptionRow>
                </div>
              </template>
            </vue-multiselect>
          </FieldView>
        </div>
        <FormErrorRightIcon />
      </float-label>
      <slot name="error" v-bind="validationProps">
        <template v-if="hasMultiSelectValidationErrors(validationProps)">
          <span class="validation-text">
            {{ getErrorFormattedText(validationProps) }}
          </span>
        </template>
      </slot>
      <slot
        v-if="
          !hasMultiSelectValidationErrors(validationProps) &&
          (subLabel || $slots.subLabel || hasTooltip)
        "
        name="subLabel"
        :has-errors="hasMultiSelectValidationErrors(validationProps)"
      >
        <FormSubLabelTooltip
          v-if="hasTooltip"
          :right-label="rightSubLabel"
          :tooltip-label="tooltipLabel"
          :left-label="subLabel"
        />
        <FormSubLabel v-else :label="subLabel" />
      </slot>
    </ValidationProvider>
  </section>
</template>

<script>
import { debounce, isEqual, isUndefined, reject } from 'lodash';
import ValidationProvider from '/@/modules/core/form/components/ValidationProvider.vue';
import { Multiselect } from 'vue-multiselect';
import { FormSelectConfig } from '/@/modules/core/form/models/FormSelectConfig';
import { FormComputedValueMixin } from '/@/modules/core/form/mixins/FormComputedValueMixin';
import FloatLabel from '/@/modules/core/form/components/FloatLabel.vue';
import { FormValidationMixin } from '/@/modules/core/form/mixins/FormValidationMixin';
import FormErrorRightIcon from '/@/modules/core/form/components/FormErrorRightIcon.vue';
import FormSubLabel from '/@/modules/core/form/components/FormSubLabel.vue';
import ModalService from '/@/modules/core/ui/services/ModalService';
import FormSubLabelTooltip from '/@/modules/core/form/components/FormSubLabelTooltip.vue';
import { FormLabelMixin } from '/@/modules/core/form/mixins/FormLabelMixin';
import { FormCypressAttribute } from '@/modules/core/form/constants/FormCypressAttribute';
import FormMultiSelectLoadMoreSection from '/@/modules/core/form/components/functional/FormMultiSelectLoadMoreSection.vue';
import FormSelectAddOptionRow from '/@/modules/core/form/components/FormSelectAddOptionRow.vue';
import { FormMultiSelectEvent } from '@/modules/core/form/constants/FormMultiSelectEvent';
import EventBus from '@/modules/core/app/helpers/EventBus';
import Logger from '@/modules/core/app/helpers/Logger';
import FieldView from '@/modules/core/ui/components/wrappers/FieldView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FormMultiSelect',
  emits: [
    'options-loaded',
    'on-add-option-click',
    'input',
    'select',
    'clear',
    'remove',
    'search-change'
  ],
  slotsDefinition: {
    option: { props: Object },
    addOption: { props: Object },
    tag: { props: Object },
    singleLabel: { props: Object },
    selection: { props: Object },
    placeholder: {},
    noOptions: {},
    noResult: {},
    error: { validationProps: Object },
    subLabel: { hasErrors: Boolean },
    emptyState: {},
    noMatchState: { search: String }
  },
  mixins: [FormComputedValueMixin, FormValidationMixin, FormLabelMixin],
  components: {
    FormMultiSelectLoadMoreSection,
    FormSelectAddOptionRow,
    FormErrorRightIcon,
    'vue-multiselect': Multiselect,
    ValidationProvider,
    FloatLabel,
    FormSubLabel,
    FormSubLabelTooltip,
    FieldView
  },
  props: {
    config: {
      type: FormSelectConfig,
      default: () => new FormSelectConfig()
    },
    queryParams: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    allowDisabledPopulation: {
      type: Boolean,
      default: false
    },
    showFloatLabel: {
      type: Boolean,
      default: true
    },
    enableAdd: {
      type: Boolean,
      default: false
    },
    pinAddOption: {
      type: Boolean,
      default: false
    },
    formMultiSelectId: {
      type: String
    },
    enableAddNewOnlyForEmptyOptions: {
      type: Boolean,
      default: false
    }
  },
  static: {
    FormCypressAttribute
  },
  data() {
    return {
      options: [],
      selectedOptions: [],
      asyncSelectedOptions: [],
      computedValueOption: null,
      isLoading: false,
      searchQuery: '',
      debouncedPopulateOptions: () => {}
    };
  },
  computed: {
    hasLoadMoreButton() {
      return this.options.length && this.options[this.options.length - 1].isLoadMoreButton;
    },
    visibleOptions() {
      return this.options.filter((option) => !option.isAddOption && !option.isLoadMoreButton);
    },
    visibleOptionsCount() {
      return this.config.groupValues
        ? this.config.getFlattenedGroupedOptions(this.visibleOptions).length
        : this.visibleOptions.length;
    },
    hasNoOptions() {
      return !this.visibleOptionsCount;
    },
    hasSearchQuery() {
      return !!this.searchQuery?.trim().length;
    },
    showEmptyState() {
      return this.pinAddOption && this.hasNoOptions && !this.hasSearchQuery;
    },
    showNoMatchState() {
      if (!this.pinAddOption || !this.hasSearchQuery || this.isLoading) {
        return false;
      }
      if (!this.config.internalSearch) {
        return this.visibleOptionsCount === 0;
      }
      return this.visibleMatchesCount === 0;
    },
    visibleMatchesCount() {
      if (!this.hasSearchQuery) {
        return this.visibleOptionsCount;
      }

      const filtered = this.$refs.multiselect?.filteredOptions ?? [];

      return filtered.filter(
        (option) => !option.$isLabel && !option.isAddOption && !option.isLoadMoreButton
      ).length;
    }
  },
  methods: {
    getClasses() {
      if (this.isLoading && this.options.length) {
        return 'is-disabled';
      }
    },
    updateAsyncOptions(asyncSelectedOptions) {
      if (!this.config.isAsync || (this.disabled && !this.allowDisabledPopulation)) {
        return;
      }

      if (this.options.at(-1).isLoadMoreButton) {
        this.asyncSelectedOptions = asyncSelectedOptions;
        this.options.splice(-1, 0, ...asyncSelectedOptions);
      }
    },
    async populateOptions() {
      if (!this.disabled || this.allowDisabledPopulation) {
        this.isLoading = true;
        const options = await this.config.getOptions(this.getQueryParams());

        if (this.config.isAsync && this.config.page > 0) {
          this.options.pop();

          if (this.asyncSelectedOptions.length) {
            // Remove all asyncSelectedOptions
            const asyncOptionsIndex = this.options.length - this.asyncSelectedOptions.length;
            this.options.splice(asyncOptionsIndex, this.asyncSelectedOptions.length);
          }

          this.options = this.options.concat(options);

          const asyncOptionIdsToRemove = [];

          this.asyncSelectedOptions.forEach((asyncOption) => {
            const trackByIds = this.options.map((option) => option[this.config.trackBy]);

            if (!trackByIds.includes(asyncOption[this.config.trackBy])) {
              // Updated options do not include the async option, so add them at the end
              this.options.push(asyncOption);
            } else {
              // Updated options now include the async option, so remove it from asyncSelectedOptions
              asyncOptionIdsToRemove.push(asyncOption[this.config.trackBy]);
            }
          });

          if (asyncOptionIdsToRemove.length) {
            this.asyncSelectedOptions = this.asyncSelectedOptions.filter(
              (asyncOption) => !asyncOptionIdsToRemove.includes(asyncOption[this.config.trackBy])
            );
          }
        } else {
          this.options = options;
        }

        this.addLoadMoreButton(options);

        await this.appendAsyncSelectedOption();

        this.setSelectedOptions(this.computedValue);

        this.isLoading = false;
        this.$emit('options-loaded', this.options);
        if (
          this.enableAdd &&
          !this.pinAddOption &&
          (!this.enableAddNewOnlyForEmptyOptions || !this.options.length)
        ) {
          this.addAddOption();
        }
      }
    },
    getQueryParams() {
      let params = { ...(this.queryParams ?? {}) };
      if (this.config.isAsync) {
        params = {
          ...params,
          page: `${this.config.page},${this.config.asyncLimit}`
        };

        if (this.config.search.length > 0) {
          params.q = this.config.search;
        }
      }

      return params;
    },
    addLoadMoreButton(options) {
      if (!this.config.isAsync) {
        return;
      }

      if (options.length === this.config.asyncLimit) {
        this.options.push({
          isLoadMoreButton: true
        });
      }
    },
    addAddOption() {
      if (this.options?.find((option) => option.isAddOption)) {
        return;
      }

      this.options.push({
        isAddOption: true
      });
    },
    async appendSingleAsyncSelectedOption() {
      let selectedOption = this.options.find(
        (option) => option[this.config.trackBy] === this.computedValue
      );

      try {
        if (!selectedOption && this.computedValue && !this.computedValueOption) {
          const value = await this.config.getOptions({
            [this.config.trackBy]: this.computedValue
          });
          if (value) {
            this.computedValueOption = value[0];
          }
        }
      } catch (e) {
        Logger.warning('failed to fetch selected option: ', this.computedValue);
      }

      if (!selectedOption && this.computedValueOption) {
        if (this.hasLoadMoreButton) {
          this.options.splice(-1, 0, this.computedValueOption);
        } else {
          this.options.push(this.computedValueOption);
        }
      }
    },
    getUnloadedOptions(options) {
      return (options ?? []).filter(
        (option) =>
          !this.options.find(
            (loadedOption) => loadedOption[this.config.trackBy] === option[this.config.trackBy]
          )
      );
    },
    async appendMultipleAsyncSelectedOption() {
      const unloadedSelectedOptions = this.getUnloadedOptions(this.computedValue);

      if (!unloadedSelectedOptions.length) {
        return;
      }

      await this.fetchMissingOptions(unloadedSelectedOptions);
      this.addFetchedOptionsToList();
    },
    async fetchMissingOptions(unloadedOptions) {
      if (!this.computedValue?.length || this.computedValueOption?.length) {
        return;
      }

      try {
        const trackingIds = unloadedOptions.map((option) => option[this.config.trackBy]);

        const fetchedValues = await this.config.getOptions({
          [this.config.trackBy]: trackingIds
        });

        if (fetchedValues.length > 0) {
          this.computedValueOption = fetchedValues;
        }
      } catch (error) {
        Logger.warning('Failed to fetch selected options:', this.computedValue);
      }
    },
    addFetchedOptionsToList() {
      const optionsToAdd = this.config.preserveSelectedOptions
        ? this.getUnloadedOptions(this.computedValueOption)
        : this.computedValueOption;

      if (!optionsToAdd?.length) {
        return;
      }

      this.asyncSelectedOptions.push(...optionsToAdd);

      if (this.hasLoadMoreButton) {
        this.options.splice(-1, 0, ...optionsToAdd);
        return;
      }

      this.options.push(...optionsToAdd);
    },
    async appendAsyncSelectedOption() {
      if (this.config.search.length > 0 || !this.config.isAsync) {
        return;
      }

      this.config.multiple
        ? await this.appendMultipleAsyncSelectedOption()
        : await this.appendSingleAsyncSelectedOption();
    },
    onLoadMore() {
      if (this.isLoading) {
        return;
      }

      this.config.incrementPage();
      this.populateOptions();
    },
    onOptionClicked(option) {
      if (option.isLoadMoreButton) {
        return;
      }
      if (this.config.isAsync) {
        this.$refs.multiselect.toggle();
      }
      if (option.isAddOption) {
        this.$emit('on-add-option-click', this.searchQuery);
        return;
      }

      if (this.config.isConfirmationRequired) {
        const confirmationModal = this.config.confirmationModalFactory.build(option);
        if (confirmationModal) {
          return this.showConfirmationModal(confirmationModal, option);
        }
      }

      this.onSelect(option);
    },
    showConfirmationModal(component, option) {
      ModalService.show({
        parent: this,
        component,
        props: { option },
        events: {
          'on-confirm': () => {
            this.onSelect(option);
          }
        }
      });
    },
    async onSelect(option) {
      await this.$refs.validationProvider.validate(option[this.config.trackBy]);
      this.selectedOptions = this.config.multiple ? this.selectedOptions.concat(option) : option;
      this.$emit(
        'input',
        this.config.multiple ? this.selectedOptions : option[this.config.trackBy]
      );
      this.$emit('select', this.selectedOptions);
      this.searchQuery = '';

      if (this.config.isAsync) {
        this.computedValueOption = this.selectedOptions;
        this.$refs.multiselect.search = '';
      }
    },
    async onRemove(option) {
      await this.$refs.validationProvider.validate(option[this.config.trackBy]);
      this.selectedOptions = Array.isArray(option)
        ? this.removeGroupOptions(option)
        : this.removeOption(option);
      this.$emit('input', this.config.multiple ? this.selectedOptions : null);
      this.$emit('clear', this.selectedOptions);
      this.$emit(this.config.multiple ? 'remove' : 'clear', this.selectedOptions);
    },
    removeOption(option) {
      return this.config.multiple
        ? reject(
            this.computedValue,
            (selectedOption) => selectedOption[this.config.trackBy] === option[this.config.trackBy]
          )
        : null;
    },
    removeGroupOptions(options) {
      const optionsToReject = options.map((option) => option[this.config.trackBy]);
      return this.config.multiple
        ? reject(this.computedValue, (selectedOption) =>
            optionsToReject.includes(selectedOption[this.config.trackBy])
          )
        : null;
    },
    getSingleSelectedOption(selectedOption) {
      if (isUndefined(selectedOption)) {
        if (this.config.preselectFirst) {
          const firstOption = this.getAllOptions()[0];
          this.$emit('input', firstOption[this.config.trackBy]);
          return firstOption;
        }
        return null;
      }

      return this.getAllOptions().find(
        (item) => JSON.stringify(item[this.config.trackBy]) === JSON.stringify(selectedOption)
      );
    },
    getMultipleSelectedOptions(selectedOptions) {
      if (!selectedOptions) {
        if (this.config.preselectFirst) {
          return [this.getAllOptions()[0]];
        }
        return [];
      }

      const allOptions = this.getAllOptions();

      if (!this.config.preserveSelectedOptions) {
        const trackByIds = selectedOptions.map(
          (selectedOption) => selectedOption[this.config.trackBy]
        );
        return allOptions.filter((item) => trackByIds.includes(item[this.config.trackBy]));
      }

      return selectedOptions
        .map(
          (selectedOption) =>
            allOptions.find(
              (option) => option[this.config.trackBy] === selectedOption[this.config.trackBy]
            ) ?? selectedOption
        )
        .filter((option) => !isUndefined(option[this.config.trackBy]));
    },
    setSelectedOptions(value) {
      this.selectedOptions = this.config.multiple
        ? this.getMultipleSelectedOptions(value)
        : this.getSingleSelectedOption(value);
    },
    getAllOptions() {
      return this.config.groupValues
        ? this.options.flatMap((option) => option[this.config.groupValues])
        : this.options;
    },
    onSearchChange(searchQuery, id) {
      this.searchQuery = searchQuery;
      if (this.config.shouldTriggerSearch(searchQuery)) {
        this.config.resetSearch(searchQuery);
        this.debouncedPopulateOptions();
      }
      this.$emit('search-change', searchQuery, id);
    },
    onAddOptionClick() {
      this.$emit('on-add-option-click', this.searchQuery);
    },
    clearSearch() {
      this.searchQuery = '';
      if (this.$refs.multiselect) {
        this.$refs.multiselect.search = '';
      }
    },
    onClose() {
      this.searchQuery = '';
      if (!this.config.isAsync) {
        return;
      }
      this.$refs.multiselect.search = '';
    }
  },
  async mounted() {
    EventBus.listenOnce(
      `${FormMultiSelectEvent.ASYNC_SELECTED_OPTION_LOADED}${this.formMultiSelectId}`,
      this.updateAsyncOptions
    );
    this.debouncedPopulateOptions = debounce(this.populateOptions, this.config.debounceTime);
    if (!this.config.searchable) {
      await this.populateOptions();
    }
  },
  watch: {
    queryParams(newParams, oldParams) {
      if (!isEqual(newParams, oldParams)) {
        this.debouncedPopulateOptions();
      }
    },
    value(value) {
      this.setSelectedOptions(value);
    },
    config() {
      this.debouncedPopulateOptions();
    }
  },
  beforeUnmount() {
    this.config.resetSearch();
    EventBus.destroy(
      `${FormMultiSelectEvent.ASYNC_SELECTED_OPTION_LOADED}${this.formMultiSelectId}`,
      this.updateAsyncOptions
    );
  }
};
</script>

<style scoped>
.plus {
  width: var(--ce-24px);
  height: var(--ce-24px);
}
.multiple-group-select :deep(.multiselect__option--group) {
  pointer-events: auto;
}
.pinned-add-option {
  position: sticky;
  bottom: 0;
  z-index: var(--ce-z-index-1);
}
</style>
